import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaSave } from "react-icons/fa";
import { Link, useParams } from "react-router";
import { z } from "zod";

import {
    PayloadDocument,
    useDroneConfigCollection,
    usePayloadDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton/index.jsx";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import Layout, { Title } from "../../components/Layout/Layout.jsx";
import urls from "../../urls.jsx";

// Use better message. The default message is 'Expected number, received nan' but that is strange because it comes from implementation details to convert text to number
const numberFromString = z.coerce.number({
    invalid_type_error: "Enter a valid number",
});

const payloadConfigSchema = z
    .object({
        targetAEV: numberFromString
            .int({ message: "Enter a whole number" })
            .min(0)
            .max(255),
        exposureUpperBound: numberFromString
            .int({ message: "Enter a whole number" })
            .positive(),
        isoLowerBound: numberFromString
            .int({ message: "Enter a whole number" })
            .positive(),
        isoUpperBound: numberFromString
            .int({ message: "Enter a whole number" })
            .positive(),
    })
    .refine((data) => data.isoUpperBound >= data.isoLowerBound, {
        message:
            "ISO upper bound must be higher than or equal to ISO lower bound",
        path: ["isoUpperBound"],
    });

export default function PayloadSettingsPage() {
    const { payloadId } = useParams();
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(payloadConfigSchema),
    });
    const [droneConfigs, isLoadingDroneConfigs] = useDroneConfigCollection();
    const payloadDroneConfigs = droneConfigs.filter(
        (droneConfig) => droneConfig.payloadId === payloadId,
    );
    const [payload, isLoadingPayload] = usePayloadDocument(payloadId, {
        mode: "once",
    });

    React.useEffect(() => {
        if (payload) {
            reset(payload);
        }
    }, [payload, reset]);

    const onSubmit = async (payloadConfigUpdate) => {
        console.log(payloadConfigUpdate);
        await PayloadDocument(payloadId).set(payloadConfigUpdate, {
            merge: true,
        });
        reset(payloadConfigUpdate);
    };

    return (
        <Layout>
            <title>{`Payload ${payload?.name} Settings | MyCorvus`}</title>
            <Breadcrumbs path={breadcrumbPaths.payload} payload={payload} />
            <Card>
                <Title
                    title={`${payload?.name || ""} Settings`}
                    backButtonUrl={urls.payloads()}
                />
                <Card.Body className="p-4 py-0">
                    {isLoadingPayload || isLoadingDroneConfigs ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <>
                            {payloadDroneConfigs.length === 0 ? (
                                "This payload is used on no drone."
                            ) : (
                                <span>
                                    This payload is used on{" "}
                                    {payloadDroneConfigs.map(
                                        (droneConfig, index, array) => (
                                            <React.Fragment
                                                key={droneConfig.id}
                                            >
                                                <Link
                                                    to={urls.locationDrone(
                                                        droneConfig.organizationId,
                                                        droneConfig.locationId,
                                                        droneConfig.id,
                                                    )}
                                                >
                                                    {droneConfig.id}
                                                </Link>
                                                {index < array.length - 1
                                                    ? ", "
                                                    : ""}
                                            </React.Fragment>
                                        ),
                                    )}
                                    .
                                </span>
                            )}
                            <p />
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <TextFormGroup
                                    label="Target AEV"
                                    name="targetAEV"
                                    control={control}
                                    errors={errors}
                                />
                                <TextFormGroup
                                    label="Exposure upper bound"
                                    name="exposureUpperBound"
                                    control={control}
                                    errors={errors}
                                    unitText="µs"
                                />
                                <TextFormGroup
                                    label="ISO lower bound"
                                    name="isoLowerBound"
                                    control={control}
                                    errors={errors}
                                />
                                <TextFormGroup
                                    label="ISO upper bound"
                                    name="isoUpperBound"
                                    control={control}
                                    errors={errors}
                                />
                                <Button
                                    variant="success"
                                    disabled={isSubmitting || !isDirty}
                                    type="submit"
                                    className="mb-3 mt-1"
                                >
                                    <FaSave className="mb-1 me-1" />
                                    {isSubmitting ? "Saving..." : "Save"}
                                </Button>
                                <CancelButton
                                    className="ms-2 mb-3 mt-1"
                                    to={urls.payloads()}
                                />
                            </Form>
                        </>
                    )}
                </Card.Body>
            </Card>
        </Layout>
    );
}
